import { useCallback, useEffect, useMemo, useRef } from 'react'
import { Map as MapboxMap, Marker, type MapRef } from 'react-map-gl/mapbox'
import { Box, Group, Paper, Text, useMantineColorScheme } from '@mantine/core'
import type { Map as MapboxGLMap } from 'mapbox-gl'
import type { MapMouseEvent } from 'react-map-gl/mapbox'
import 'mapbox-gl/dist/mapbox-gl.css'
import { MAPBOX_PUBLIC_TOKEN } from '@/constants'
import { determineMapCenter } from '@/components/Navigation/utils/mapUtils'
import type { DistrictWithBoundaries, ScheduledDriveResponseWithEndpoints } from '@/api/main/types.gen'
import { boundsFromDistrictCollection, districtsToFeatureCollection } from '@/utils/autoScheduleDistricts'
import { Layer, Source } from 'react-map-gl/mapbox'
import { useTranslation } from 'react-i18next'
import checkeredCircle from '@images/checkeredCircle.png'

const FILL_LAYER_ID = 'auto-schedule-districts-fill'
const LINE_LAYER_ID = 'auto-schedule-districts-line'
const SOURCE_ID = 'auto-schedule-districts'
const THIS_VEHICLE_COLOR = '#fa5252'
const OTHER_VEHICLE_COLOR = '#868e96'
const ENDPOINT_DOT_SIZE = 10
const DESTINATION_FLAG_SIZE = 7

const endpointDotStyle = (color: string) => ({
  width: ENDPOINT_DOT_SIZE,
  height: ENDPOINT_DOT_SIZE,
  borderRadius: '50%',
  background: color,
  border: '1px solid rgba(255,255,255,0.7)',
  boxSizing: 'border-box' as const,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  flexShrink: 0,
})

function EndpointDot({ color, flag }: { color: string; flag?: boolean }) {
  return (
    <Box data-endpoint-dot style={endpointDotStyle(color)}>
      {flag ? (
        <img
          src={checkeredCircle}
          alt=""
          width={DESTINATION_FLAG_SIZE}
          height={DESTINATION_FLAG_SIZE}
          style={{ display: 'block', borderRadius: '50%' }}
        />
      ) : null}
    </Box>
  )
}

export function ScheduledDriveLegend() {
  const { t } = useTranslation('scheduling')
  return (
    <Paper
      shadow="sm"
      p={6}
      radius="sm"
      style={{
        position: 'absolute',
        top: 8,
        left: 8,
        zIndex: 2,
        pointerEvents: 'none',
        background: 'light-dark(rgba(255,255,255,0.92), rgba(24,24,27,0.92))',
      }}
    >
      <Box component="ul" m={0} p={0} style={{ listStyle: 'none' }}>
        {(
          [
            [THIS_VEHICLE_COLOR, 'origin', t('autoSchedule.legendThisStart')],
            [THIS_VEHICLE_COLOR, 'destination', t('autoSchedule.legendThisEnd')],
            [OTHER_VEHICLE_COLOR, 'origin', t('autoSchedule.legendOtherStart')],
            [OTHER_VEHICLE_COLOR, 'destination', t('autoSchedule.legendOtherEnd')],
          ] as const
        ).map(([color, kind, label]) => (
          <Box component="li" key={label} py={2}>
            <Group gap={6} wrap="nowrap">
              <EndpointDot color={color} flag={kind === 'destination'} />
              <Text size="xs" lh={1.2}>
                {label}
              </Text>
            </Group>
          </Box>
        ))}
      </Box>
    </Paper>
  )
}

const featureId = (districtId: number | string): number | undefined => {
  const parsed = Number(districtId)
  return Number.isFinite(parsed) ? parsed : undefined
}

const districtIdFromEvent = (event: MapMouseEvent): number | undefined => {
  const districtId = event.features?.[0]?.properties?.districtId
  if (typeof districtId === 'number' || typeof districtId === 'string') {
    return featureId(districtId)
  }
  return undefined
}

export function DistrictPolygonsMap({
  districts,
  selectedIds,
  hoveredId,
  onToggle,
  onHover,
  drives,
  vehicleId,
}: {
  districts: DistrictWithBoundaries[]
  selectedIds: number[]
  hoveredId: number | undefined
  onToggle: (districtId: number) => void
  onHover: (districtId: number | undefined) => void
  drives?: ScheduledDriveResponseWithEndpoints[]
  vehicleId?: string
}) {
  const { colorScheme } = useMantineColorScheme()
  const mapRef = useRef<MapRef>(null)
  const lastHoverRef = useRef<number | undefined>(undefined)
  const appliedHoverRef = useRef<number | undefined>(undefined)
  const appliedSelectedRef = useRef<Set<number>>(new Set())
  const selectionRef = useRef({ selectedIds, hoveredId })
  selectionRef.current = { selectedIds, hoveredId }
  const collection = useMemo(() => districtsToFeatureCollection(districts, [], undefined), [districts])
  const lastCollectionRef = useRef(collection)
  const initialViewState = useMemo(() => {
    const bounds = boundsFromDistrictCollection(collection)
    if (bounds) {
      return { bounds, fitBoundsOptions: { padding: 40, maxZoom: 13 } }
    }
    return { zoom: 11, ...determineMapCenter() }
  }, [collection])

  const mapStyle = useMemo(
    () => `mapbox://styles/mapbox/${colorScheme === 'light' || colorScheme === 'auto' ? 'streets-v12' : 'dark-v11'}`,
    [colorScheme]
  )

  const handleClick = useCallback(
    (event: MapMouseEvent) => {
      const districtId = districtIdFromEvent(event)
      if (districtId !== undefined) {
        onToggle(districtId)
      }
    },
    [onToggle]
  )

  const handleMove = useCallback(
    (event: MapMouseEvent) => {
      const districtId = districtIdFromEvent(event)
      if (lastHoverRef.current === districtId) {
        return
      }
      lastHoverRef.current = districtId
      onHover(districtId)
    },
    [onHover]
  )

  const handleLeave = useCallback(() => {
    if (lastHoverRef.current === undefined) {
      return
    }
    lastHoverRef.current = undefined
    onHover(undefined)
  }, [onHover])

  const applyFeatureStates = useCallback((map: MapboxGLMap, sourceChanged = false) => {
    if (!map.getSource(SOURCE_ID)) {
      return
    }
    if (sourceChanged) {
      appliedHoverRef.current = undefined
      appliedSelectedRef.current = new Set()
    }
    const { selectedIds: currentSelected, hoveredId: currentHovered } = selectionRef.current
    const selected = new Set(currentSelected)
    const setState = (id: number, state: { hovered?: boolean; selected?: boolean }) => {
      map.setFeatureState({ source: SOURCE_ID, id }, state)
    }
    if (appliedHoverRef.current !== currentHovered) {
      if (appliedHoverRef.current !== undefined) {
        setState(appliedHoverRef.current, { hovered: false })
      }
      if (currentHovered !== undefined) {
        setState(currentHovered, { hovered: true })
      }
      appliedHoverRef.current = currentHovered
    }
    appliedSelectedRef.current.forEach(id => {
      if (!selected.has(id)) {
        setState(id, { selected: false })
      }
    })
    selected.forEach(id => {
      if (!appliedSelectedRef.current.has(id)) {
        setState(id, { selected: true })
      }
    })
    appliedSelectedRef.current = selected
  }, [])

  useEffect(() => {
    const map = mapRef.current?.getMap()
    if (!map) {
      return
    }
    const sourceChanged = lastCollectionRef.current !== collection
    lastCollectionRef.current = collection
    applyFeatureStates(map, sourceChanged)
  }, [hoveredId, selectedIds, collection, applyFeatureStates])

  return (
    <Box pos="relative" w="100%" h="100%">
      <MapboxMap
        ref={mapRef}
        mapboxAccessToken={MAPBOX_PUBLIC_TOKEN}
        mapStyle={mapStyle}
        initialViewState={initialViewState}
        style={{ width: '100%', height: '100%' }}
        interactiveLayerIds={[FILL_LAYER_ID]}
        cursor={hoveredId === undefined ? 'grab' : 'pointer'}
        onClick={handleClick}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        onLoad={event => {
          event.target.resize()
          applyFeatureStates(event.target, true)
        }}
      >
        <Source id={SOURCE_ID} type="geojson" data={collection} promoteId="districtId">
          <Layer
            id={FILL_LAYER_ID}
            type="fill"
            paint={{
              'fill-color': [
                'case',
                ['boolean', ['feature-state', 'hovered'], false],
                '#fcc419',
                ['boolean', ['feature-state', 'selected'], false],
                '#be4bdb',
                '#1c7ed6',
              ],
              'fill-opacity': [
                'case',
                ['boolean', ['feature-state', 'hovered'], false],
                0.55,
                ['boolean', ['feature-state', 'selected'], false],
                0.45,
                0.28,
              ],
            }}
          />
          <Layer
            id={LINE_LAYER_ID}
            type="line"
            paint={{
              'line-color': [
                'case',
                ['boolean', ['feature-state', 'hovered'], false],
                '#fab005',
                ['boolean', ['feature-state', 'selected'], false],
                '#9c36b5',
                '#ffa94d',
              ],
              'line-width': 1.5,
            }}
          />
        </Source>
        {(drives ?? []).flatMap(drive => {
          const markers = []
          const color = drive.vehicle_id === vehicleId ? THIS_VEHICLE_COLOR : OTHER_VEHICLE_COLOR
          if (drive.origin_point) {
            markers.push(
              <Marker
                key={`${drive.id}-origin`}
                longitude={drive.origin_point.longitude}
                latitude={drive.origin_point.latitude}
                anchor="center"
              >
                <EndpointDot color={color} />
              </Marker>
            )
          }
          if (drive.destination_point) {
            markers.push(
              <Marker
                key={`${drive.id}-destination`}
                longitude={drive.destination_point.longitude}
                latitude={drive.destination_point.latitude}
                anchor="center"
              >
                <EndpointDot color={color} flag />
              </Marker>
            )
          }
          return markers
        })}
      </MapboxMap>
      <ScheduledDriveLegend />
    </Box>
  )
}
